import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { Breadcrumbs } from '../../components/guide/Breadcrumbs';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { EntryBlocks, LinkCard } from '../../components/guide/entry/EntryBlocks';
import { EntryToc } from '../../components/guide/entry/EntryToc';
import { FaqList } from '../../components/guide/FaqList';
import { EntityThumb } from '../../components/world/EntityThumb';
import { getEntry, getCategory, faqsForEntry, entryKindLabel } from '../../utils/guideIndex';
function EntrySectionShell({
  id,
  title,
  children
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} aria-labelledby={`${id}-t`} className="scroll-mt-32 -mx-3 rounded-3xl px-3 py-6 md:-mx-4 md:px-4 md:py-8" data-id="element-2810" data-mp="1flr5dkb9peyt">
      <div className="mb-4 flex items-center gap-2" data-id="element-2811" data-mp="39xxrhe5p9g5w">
        <h2 id={`${id}-t`} className="font-cinzel text-xl md:text-2xl font-bold text-white" data-id="element-2812" data-mp="39z8uhbrbmjvy">{title}</h2>
        <CopyLinkButton hash={id} iconOnly label={`Copiar enlace a “${title}”`} data-id="element-2813" data-mp="2to7xo2yk8k5i" />
      </div>
      <div className="text-[15px] md:text-base leading-relaxed text-gray-300" data-id="element-2814" data-mp="2xs5w0bg29qxi">{children}</div>
    </section>;
}
export function GuideEntryPage() {
  const {
    categoryId = '',
    entryId = ''
  } = useParams();
  const entry = getEntry(entryId);
  useEffect(() => {
    if (entry) document.title = `${entry.title} · Guía MU Vanguard`;
  }, [entry]);
  if (!entry || entry.categoryId !== categoryId) {
    return <div className="rounded-3xl border border-white/10 bg-[#0A1628] p-8" data-id="element-2815" data-mp="3heu7nzs4kei5">
        <h1 className="font-cinzel text-2xl font-bold text-white" data-id="element-2816" data-mp="32pfwcm4znn04">Ficha no encontrada</h1>
        <p className="mt-2 text-gray-400" data-id="element-2817" data-mp="37osfavsqo9xf">Esta ficha no existe o todavía no está publicada.</p>
        <Link to="/guia" className="mt-5 inline-flex font-semibold text-[#D4AF37]" data-id="element-2818" data-mp="3vev5f6d3f5te">Volver a la guía</Link>
      </div>;
  }
  if (entry.customPath) return <Navigate to={entry.customPath} replace data-id="element-2819" data-mp="42k2mpmommnh" />;
  const cat = getCategory(entry.categoryId);
  const faqs = faqsForEntry(entry.id);
  const related = (entry.related ?? []).filter(id => getEntry(id));
  const toc = [...entry.sections.map(s => ({
    id: s.id,
    title: s.title
  })), ...(faqs.length ? [{
    id: 'preguntas',
    title: 'Preguntas frecuentes'
  }] : []), ...(related.length ? [{
    id: 'relacionados',
    title: 'Contenido relacionado'
  }] : [])];
  return <article data-id="element-2820" data-mp="3r3rcx0y9eth6">
      <Breadcrumbs items={[{
      label: 'Guía',
      to: '/guia'
    }, ...(cat ? [{
      label: cat.title,
      to: `/guia/${cat.id}`
    }] : []), {
      label: entry.title
    }]} data-id="element-2821" data-mp="3r4kdfk6mkmqv" />

      <header className="border-b border-white/10 pb-8" data-id="element-2822" data-mp="3hdfu689x2fq8">
        <div className="flex flex-wrap items-center gap-2" data-id="element-2823" data-mp="3ceuuqb2ho02u">
          <span className="rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-gray-300" data-id="element-2824" data-mp="3cc8oqfv8xsmq">{entryKindLabel(entry)}</span>
          {entry.status === 'available' ? <span className="rounded-full bg-[#3BA55C]/15 px-3 py-1 text-xs font-bold text-[#5FCB7F]" data-id="element-2825" data-mp="33e6s0cijpm59">Disponible</span> : <span className="rounded-full bg-[#4A9EFF]/15 px-3 py-1 text-xs font-bold text-[#4A9EFF]" data-id="element-2826" data-mp="1nufjvpa3vnh5">Anunciado · aún no disponible</span>}
        </div>
        <div className="mt-4 flex flex-col gap-5 md:flex-row md:items-start" data-id="element-2827" data-mp="1rwkfgtk46u6f">
          {entry.entity ? <EntityThumb entity={entry.entity} name={entry.title} size="hero" placeholder lazy={false} data-id="element-2828" data-mp="1wuccg1zm96hh" /> : entry.image && <img src={entry.image} alt={entry.title} className="h-24 w-24 shrink-0 rounded-2xl border border-[#D4AF37]/30 bg-[#0A1628] object-cover object-top" data-id="element-2829" data-mp="1466rbe4nhfh4" />}
          <div className="min-w-0 flex-1" data-id="element-2830" data-mp="193q6qhd33yan">
            <h1 className="font-cinzel text-white text-3xl sm:text-4xl md:text-5xl font-black leading-tight" data-id="element-2831" data-mp="1dwjab1wth0l5">{entry.title}</h1>
            <p className="mt-3 max-w-2xl text-[15px] md:text-lg text-gray-300 leading-relaxed" data-id="element-2832" data-mp="ggchl5fmo713">{entry.summary}</p>
            {entry.aliases && entry.aliases.length > 0 && <p className="mt-2 text-sm text-gray-500" data-id="element-2833" data-mp="2o3p2ftk5blev">También conocido como: {entry.aliases.join(', ')}</p>}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4" data-id="element-2834" data-mp="2j1px4e4uiefa">
          <CopyLinkButton data-id="element-2835" data-mp="2wvqx1b7ja7h8" />
          {entry.updatedAt && <span className="flex items-center gap-2 text-sm text-gray-500" data-id="element-2836" data-mp="2s3pg3bzf7skv">
              <CalendarClock className="h-4 w-4" aria-hidden="true" data-id="element-2837" data-mp="36t544k7khjwv" />
              Actualizado: {entry.updatedAt}
            </span>}
        </div>
      </header>

      <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_200px]" data-id="element-2838" data-mp="31tsiu3wcgkwe">
        <div className="min-w-0" data-id="element-2839" data-mp="3fss7vcnrr1t3">
          {toc.length >= 3 && <div className="mt-6 xl:hidden" data-id="element-2840" data-mp="3bshs58a6cx11">
              <EntryToc items={toc} variant="inline" data-id="element-2841" data-mp="3bsq4mz2dy0bp" />
            </div>}

          <div className="divide-y divide-white/10" data-id="element-2842" data-mp="1lgu07f53ydzb">
            {entry.sections.map(s => <EntrySectionShell key={s.id} id={s.id} title={s.title} data-id="element-2843" data-mp="1hf8o3w0ee3q6">
                <EntryBlocks blocks={s.blocks} data-id="element-2844" data-mp="17aif566ktzcg" />
              </EntrySectionShell>)}
            {faqs.length > 0 && <EntrySectionShell id="preguntas" title="Preguntas frecuentes" data-id="element-2845" data-mp="1bakakxlpxwja">
                <FaqList faqs={faqs} data-id="element-2846" data-mp="xl8rc2irn2u5" />
              </EntrySectionShell>}
            {related.length > 0 && <EntrySectionShell id="relacionados" title="Contenido relacionado" data-id="element-2847" data-mp="12i8v26s57ti3">
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-2848" data-mp="nl83q4dv60k5">
                  {related.map(id => <LinkCard key={id} link={{
                entryId: id
              }} data-id="element-2849" data-mp="slvtynu9qddb" />)}
                </div>
              </EntrySectionShell>}
          </div>
        </div>
        {toc.length >= 3 && <aside className="hidden xl:block pt-8" data-id="element-2850" data-mp="jked3391e7nk">
            <EntryToc items={toc} variant="side" data-id="element-2851" data-mp="jl7mkbzdrb9e" />
          </aside>}
      </div>
    </article>;
}